<template>
  <div>
    <div
      v-if="selectedFilters.length > 0 || productSearch || filteredProducts.length > 0"
      class="flex px-3 items-center gap-2 md:flex-wrap md:py-6 overflow-hidden w-full overflow-x-scroll md:overflow-auto"
    >
      <!-- Existing filter tags -->
      <div
        v-for="filter in selectedFilters"
        :key="filter"
        v-show="filter && !!filter.title"
        class="bg-secondary text-white max-w-md text-xs px-3 py-2 rounded-full flex items-center space-x-2 break-keep"
      >
        <span class="whitespace-nowrap">{{ filter.title }}</span>
        <button @click="clearSingleFilter(filter)">
          <XIcon class="w-4 h-4 cursor-pointer" />
        </button>
      </div>
      <div
        v-if="productSearch"
        class="bg-secondary text-white max-w-md text-xs px-3 py-2 rounded-full flex items-center space-x-2 break-keep"
      >
        <span class="whitespace-nowrap">{{ productSearch }}</span>
        <button @click="clearSearch()">
          <XIcon class="w-4 h-4 cursor-pointer" />
        </button>
      </div>
      <a
        @click="clearAllFilters()"
        v-if="selectedFilters.length > 2"
        class="underline text-secondary text-xs cursor-pointer whitespace-nowrap"
      >
        {{ $t("filter.clearAll") }}
      </a>
    </div>
    <div v-else class="md:py-10 flex"></div>

    <NuxtLayout name="center">
      <!-- Modified  to use filteredProducts -->
      <div class="flex flex-wrap md:mt-0 mt-6 bg-white" v-if="filteredProducts.length > 0">
        <div
          v-for="product in filterPagination(slicedIndex)"
          :key="product.id"
          class="product-card-col2 flex basis-full md:basis-1/2 mb-8 transition-shadow duration-400 ease-out"
        >
          <ProductCard :item="product" />
        </div>
      </div>
      <div v-else class="w-full h-[40vh] flex justify-center items-center">
        <span>{{ $t("testPanel.noResult") }}</span>
      </div>

      <!-- Product pagination -->
      <div
        v-if="filteredProducts.length > 12 && slicedIndex < filteredProducts.length"
        class="flex justify-center w-full md:my-12 mt-10 mb-12"
      >
        <button @click="showMore" class="base-outline-btn w-40 mx-auto font-semibold">
          {{ $t("filter.showMore") }}
        </button>
      </div>
    </NuxtLayout>
  </div>
</template>
<script setup>
import { XIcon } from "@heroicons/vue/outline";

const props = defineProps({
  categoryParams: {
    type: Object,
  },
});

const updatedObject = {
  ...props.categoryParams,
  ...{ hidden_tests: false },
};

const { data: productsList } = await useFetchProductList(updatedObject);
const products = computed(() => productsList.value.data);
const filterStore = useProductStore();
const selectedFilters = computed({
  get() {
    return filterStore.getCheckedFilter.filter((item) => item !== undefined);
  },
});

const min = computed(() => filterStore.min);
const max = computed(() => filterStore.max);
const productSearch = computed({
  get() {
    if (filterStore.search == "") {
      filterStore.setSortOption(2);
    } else {
      filterStore.setSortOption(0);
    }
    return filterStore.search;
  },
  set(val) {
    filterStore.setSearchValue(val);
  },
});
const checked = computed(() => filterStore.checked);
const slicedIndex = ref(12);
const categorySelected = computed(() => filterStore.category);
const subCategorySelected = computed(() => filterStore.subcategory);
const serviceSelected = computed(() => filterStore.service.filter((item) => item !== undefined));
const collectionSelected = computed(() => filterStore.collection);
const testTypesSelected = computed(() => filterStore.test_type);
const infectionSelected = computed(() => filterStore.infection);

onUnmounted(() => {
  clearAllFilters();
});

// Filter Products
const filteredProducts = computed(() => {
  let tempProducts = products.value.map((data) => ({
    ...data,
    price: Number(data.price),
  }));
  // Filter by sort option
  switch (filterStore.selectedSortOption) {
    // SORT BY LOW TO HIGH
    case 0:
      tempProducts = tempProducts.sort((a, b) => (a.title > b.title ? 1 : -1)).sort((a, b) => a.price - b.price);
      break;

    // SORT BY HIGH TO LOW
    case 1:
      tempProducts = tempProducts.sort((a, b) => b.price - a.price);
      break;

    // SORT BY ORDER BY RECOMMENDED
    case 2:
      tempProducts = products.value.map((item) => item);
      break;

    // SORT BY NAME-ASCENDING
    case 3:
      tempProducts = tempProducts.sort((a, b) => (a.title > b.title ? 1 : -1));
      break;

    // SORT BY NAME-DESCENDING
    case 4:
      tempProducts = tempProducts.sort((a, b) => (a.title < b.title ? 1 : -1));
      break;

    // DEFAULT SORT BY ORDER BY RECOMMENDED
    default:
      tempProducts = products.value.map((item) => item);
      break;
  }
  // Filter by price Range
  tempProducts = tempProducts.filter((item) => {
    return item.price >= min.value && item.price <= max.value;
  });

  // Filter by Search
  if (productSearch.value != "" && productSearch.value) {
    tempProducts = tempProducts.filter((item) => {
      var infectionsExits = false;
      if (item.infections.length > 0) {
        item.infections.forEach((infection, index) => {
          if (
            infection.title.toUpperCase().includes(productSearch.value.toUpperCase()) ||
            infection.slug.toUpperCase().includes(productSearch.value.toUpperCase())
          ) {
            infectionsExits = true;
          }
        });
      }

      return (
        item.title.toUpperCase().includes(productSearch.value.toUpperCase()) ||
        (item.summary != null && item.summary.toUpperCase().includes(productSearch.value.toUpperCase())) ||
        infectionsExits
      );
    });
  }

  // Filter By category
  if (categorySelected.value.length > 0 && checked.value.length > 0) {
    tempProducts = tempProducts.filter((item) =>
      item.categories.some((category) => categorySelected.value.map((data) => data.slug).includes(category.slug))
    );
  }

  // Filter By Sub Category
  if (subCategorySelected.value.length > 0 && checked.value.length > 0) {
    tempProducts = tempProducts.filter((item) =>
      item.categories.some((category) => subCategorySelected.value.map((data) => data.slug).includes(category.slug))
    );
  }

  // Filter By service
  if (serviceSelected.value.length > 0 && checked.value.length > 0) {
    tempProducts = tempProducts.filter((item) => {
      return checked.value.includes(item.service_type.slug);
    });
  }

  // Filter by collection
  if (collectionSelected.value.length > 0 && checked.value.length > 0) {
    tempProducts = tempProducts.filter((item) =>
      item.collection_methods.some((collection) => checked.value.includes(collection.slug))
    );
  }
  // Filter by types
  if (testTypesSelected.value.length > 0 && checked.value.length > 0) {
    tempProducts = tempProducts.filter((item) => {
      return checked.value.includes(item.test_type.slug);
    });
  }

  // Filter by infection

  //  filterStore.setCollection(filteredValue,'infection');
  if (infectionSelected.value.length > 0 && checked.value.length > 0) {
    if (filterStore.infectionSortType == "AND") {
      tempProducts = tempProducts.filter((item) =>
        Object.values(infectionSelected.value).every((infection) =>
          item.infections.map((data) => data.slug).includes(infection.slug)
        )
      );
    } else {
      tempProducts = tempProducts.filter((item) =>
        item.infections.some((infection) => Object.values(infectionSelected.value).includes(infection.slug))
      );
    }
  }
  filterStore.setFiltredTests(tempProducts);
  return tempProducts;
});
const filterPagination = (index) => {
  return filteredProducts.value.slice(0, index);
};
// Show more
const showMore = () => {
  slicedIndex.value += 12;
};

// Clear Single Filter
const clearSingleFilter = (item) => {
  filterStore.clearSingleItem(item);
};

function removeCategory(item) {
  filterStore.removeCategory(item);
  filterStore.removeChecked(item.slug);
}

// Clear Search
function clearSearch() {
  filterStore.setSortOption(2);
  //document.getElementById("search-product").value = "";
  filterStore.setSearchValue("");
}
const clearAllFilters = () => {
  clearSearch();
  filterStore.clearAllFilters();
};
</script>
